import type { AdjustmentType, ExtractDocumentResponse } from '@eriko/shared-types';
import { formatCurrency, formatDate } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { listDocuments } from '../../api/documents';
import { extractDocument } from '../../api/reconciliation';
import { looksLikeTaxForm } from '../../pages/documents/guessDocType';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useAttachReceipt } from '../../hooks/useReconciliation';
import { useAuthStore } from '../../stores/authStore';

/** Minimum surface the dialog needs from a queue row. */
export interface AttachReceiptTarget {
  transactionId: string;
  bankTransaction: {
    description: string;
    amount: string;
    date: string;
    type?: 'DEBIT' | 'CREDIT';
  };
  /** ISO 4217 code for the bank-line currency (e.g. "USD", "GBP"). */
  currency?: string;
}

/** Payload reported back to the chat card after a successful attach so it
 *  can collapse the row into a confirmation strip in place. */
export interface AttachReceiptResolution {
  matchedSummary: string;
  matchedAmount: string | null;
  variance: number | null;
}

interface Props {
  item: AttachReceiptTarget;
  onClose: () => void;
  /** Called after a successful attach. The chat card uses this to replace
   *  the row's action buttons with an inline confirmation strip. */
  onAttached?: (payload: AttachReceiptResolution) => void;
}

interface NormalizedFields {
  vendorName: string | null;
  totalAmount: string | null;
  documentDate: string | null;
  currency: string | null;
}

const ADJUSTMENT_TYPE_LABELS: Record<AdjustmentType, string> = {
  PROCESSING_FEE: 'Processing fee',
  BANK_FEE: 'Bank fee',
  FX_DIFFERENCE: 'FX difference',
  DISCOUNT: 'Discount',
  WRITE_OFF: 'Write-off',
  INTEREST_EARNED: 'Interest earned',
  ROUNDING: 'Rounding',
  OTHER: 'Other',
};

/**
 * SME-friendly attach-receipt dialog with auto-extract + variance + adjustment.
 *
 * Flow:
 *  1. SME picks an existing receipt OR uploads a new file.
 *  2. After selection/upload, the dialog calls `POST /documents/:id/extract`
 *     to OCR the file and persist `vendorName / totalAmount / documentDate` to
 *     the Document row. Synchronous wait (5–15s for new uploads, instant for
 *     already-PROCESSED docs).
 *  3. Variance is computed (bank-line amount − receipt total). If non-zero,
 *     SME can optionally explain it via the adjustment section (processing
 *     fee, FX, discount, write-off, rounding, etc.).
 *  4. Submit creates a CONFIRMED RECEIPT match (DOCUMENT-type fast-path),
 *     optionally with adjustment captured for reviewer awareness.
 */
export function AttachReceiptDialog({ item, onClose, onAttached }: Props) {
  const orgId = useAuthStore((s) => s.organization?.id);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [extractedFields, setExtractedFields] = useState<NormalizedFields | null>(null);
  const [resolvedParty, setResolvedParty] = useState<ExtractDocumentResponse['resolvedParty']>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  // Guard against state updates after unmount (dialog closed mid-extract).
  const mountedRef = useRef(true);
  // Set true on mount AND on every re-mount (StrictMode double-invokes effects
  // in dev — without re-setting to true, the second cleanup permanently flips
  // the ref to false and every guarded setState is skipped → "Reading the
  // receipt…" spinner gets stuck because setIsExtracting(false) never runs).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Scoped to receipts not yet reconciled — a receipt already attached to a
  // confirmed match is invalid as a candidate for a different bank line.
  // Server applies the exclusion (org-wide); separate query so the global
  // `useDocuments` (Documents page) still shows every upload.
  // orgId in the key prevents cross-org cache bleed if the user switches orgs.
  const { data: documents = [] } = useQuery({
    queryKey: ['documents', 'available', orgId],
    queryFn: () => listDocuments({ excludeMatched: true }),
    staleTime: 15_000,
    enabled: !!orgId,
  });
  const upload = useFileUpload({ linkedConversationId: null });
  const attach = useAttachReceipt();
  const [submitting, setSubmitting] = useState(false);

  // Adjustment UI
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('PROCESSING_FEE');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');

  const bankAmount = parseFloat(item.bankTransaction.amount);

  // SMEs pick from their uploaded receipts. We don't show statements, bills, or
  // tax forms here — a Schedule C / VAT return / 1099 is never a receipt for a
  // bank line. Tax forms are excluded both by docType (TAX_DOC) and, defensively,
  // by filename for docs the upload guesser stored as OTHER (`looksLikeTaxForm`).
  // Each doc is scored by amount-match % vs the bank line, then sorted so the
  // best candidate sits at the top (parity with MatchToExistingDialog).
  const candidateDocs = useMemo(() => {
    const scoreOf = (amt: string | null | undefined): number | null => {
      if (!amt) return null;
      const n = parseFloat(amt);
      if (Number.isNaN(n) || Number.isNaN(bankAmount)) return null;
      const base = Math.max(Math.abs(bankAmount), Math.abs(n));
      if (base === 0) return 100;
      const diffRatio = Math.abs(bankAmount - n) / base;
      return Math.max(0, Math.round(100 * (1 - diffRatio)));
    };

    const filtered = documents
      .filter(
        (d) =>
          d.docType === 'RECEIPT' ||
          (d.docType === 'OTHER' && !looksLikeTaxForm(d.fileName)),
      )
      .map((d) => ({ ...d, matchPercent: scoreOf(d.totalAmount) }));

    filtered.sort((a, b) => (b.matchPercent ?? -1) - (a.matchPercent ?? -1));

    return filtered;
  }, [documents, bankAmount]);

  const receiptAmount = extractedFields?.totalAmount
    ? parseFloat(extractedFields.totalAmount)
    : null;
  const variance =
    receiptAmount != null && !Number.isNaN(bankAmount) && !Number.isNaN(receiptAmount)
      ? +(bankAmount - receiptAmount).toFixed(2)
      : null;
  const varianceAbs = variance != null ? Math.abs(variance) : null;
  const needsAdjustment = varianceAbs != null && varianceAbs >= 0.01;
  const receiptMatchPercent = (() => {
    if (receiptAmount == null || Number.isNaN(bankAmount) || Number.isNaN(receiptAmount)) return null;
    const base = Math.max(Math.abs(bankAmount), Math.abs(receiptAmount));
    if (base === 0) return 100;
    return Math.max(0, Math.round(100 * (1 - Math.abs(bankAmount - receiptAmount) / base)));
  })();

  // Trigger extraction. Used after selecting an existing doc OR after upload.
  const runExtract = async (docId: string) => {
    setIsExtracting(true);
    setExtractError(null);
    setExtractedFields(null);
    try {
      const res = await extractDocument(docId);
      if (mountedRef.current) {
        setExtractedFields({
          vendorName: res.normalized?.vendorName ?? null,
          totalAmount: res.normalized?.totalAmount ?? null,
          documentDate: res.normalized?.documentDate ?? null,
          currency: res.normalized?.currency ?? null,
        });
        setResolvedParty(res.resolvedParty ?? null);
      }
    } catch (err) {
      if (mountedRef.current) {
        setExtractError(err instanceof Error ? err.message : 'Could not read the receipt');
      }
    } finally {
      if (mountedRef.current) {
        setIsExtracting(false);
      }
    }
  };

  const handleSelectDoc = async (docId: string) => {
    setSelectedDocId(docId);
    // Reset adjustment + resolved party state for the new pick.
    setAdjustmentOpen(false);
    setAdjustmentAmount('');
    setResolvedParty(null);
    // Always call extract — the backend short-circuits on already-PROCESSED docs
    // (returns cached: true instantly) so resolvedParty is always hydrated.
    await runExtract(docId);
  };

  const submit = async () => {
    if (!selectedDocId) return;
    setSubmitting(true);
    try {
      await attach.mutateAsync({
        transactionId: item.transactionId,
        documentId: selectedDocId,
        ...(adjustmentOpen && adjustmentAmount.trim()
          ? {
              adjustment: {
                type: adjustmentType,
                amount: adjustmentAmount.trim(),
                description: adjustmentDescription.trim() || undefined,
              },
            }
          : {}),
      });
      // Report the resolution back to the parent so the chat-card row
      // collapses into a Wonso-style confirmation strip in place.
      const summary =
        resolvedParty?.displayName
        ?? extractedFields?.vendorName
        ?? documents.find((d) => d.id === selectedDocId)?.vendorName
        ?? documents.find((d) => d.id === selectedDocId)?.fileName
        ?? 'Receipt';
      onAttached?.({
        matchedSummary: summary,
        matchedAmount: extractedFields?.totalAmount ?? null,
        variance: variance,
      });
      toast.success(
        resolvedParty?.displayName
          ? `${resolvedParty.displayName} receipt attached.`
          : extractedFields?.vendorName
            ? `${extractedFields.vendorName} receipt attached.`
            : 'Receipt attached.',
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not attach receipt');
    } finally {
      setSubmitting(false);
    }
  };

  const submitUpload = async (file: File) => {
    setResolvedParty(null);
    setSubmitting(true);
    try {
      const presign = await upload.uploadOne(file, { docType: 'RECEIPT' });
      if (!presign?.documentId) {
        toast.error('Upload succeeded but no document id returned. Try again.');
        return;
      }
      setSelectedDocId(presign.documentId);
      // Auto-extract immediately after upload — SME wants the dialog to show
      // vendor/amount/date so they can review variance before attaching.
      await runExtract(presign.documentId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-0 border border-surface-200 rounded-lg shadow-2xl p-6 max-w-xl w-full max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-surface-900 mb-1">Attach a receipt</h3>
        <p className="text-xs text-surface-500 mb-4">
          For: <span className="font-mono">{item.bankTransaction.description}</span> ·{' '}
          <span className="font-mono">{formatCurrency(bankAmount, 2, item.currency)}</span> ·{' '}
          {formatDate(item.bankTransaction.date)}
        </p>

        <section className="mb-5">
          <h4 className="text-xs font-semibold text-surface-700 mb-2">
            Pick a receipt you&apos;ve uploaded
          </h4>
          {candidateDocs.length === 0 ? (
            <p className="text-xs text-surface-500 italic">
              No receipts in your library yet. Upload one below.
            </p>
          ) : (
            <ul className="space-y-1 max-h-44 overflow-auto border border-surface-200 rounded-md divide-y divide-surface-100">
              {candidateDocs.map((d) => (
                <li key={d.id}>
                  <label
                    className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors hover:bg-surface-50 ${
                      selectedDocId === d.id ? 'bg-surface-100' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="receipt"
                      value={d.id}
                      checked={selectedDocId === d.id}
                      onChange={() => void handleSelectDoc(d.id)}
                      className="mt-1 accent-surface-900"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium text-surface-900 truncate">
                          {d.vendorName ?? d.fileName}
                        </span>
                        {d.matchPercent != null && (
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-medium tabular-nums ${
                              d.matchPercent === 100
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                : d.matchPercent >= 90
                                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                                  : 'border-surface-200 text-surface-500'
                            }`}
                          >
                            {d.matchPercent}% match
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-surface-500 mt-0.5 font-mono tabular-nums">
                        {d.totalAmount
                          ? formatCurrency(parseFloat(d.totalAmount), 2, d.currency ?? item.currency)
                          : '— pending parse —'}
                        {d.documentDate ? ` · ${formatDate(d.documentDate)}` : ''}
                      </div>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border-t border-surface-200 pt-4 mb-4">
          <h4 className="text-xs font-semibold text-surface-700 mb-2">Or upload a new one</h4>
          <label className="block">
            <span className="sr-only">Receipt file</span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
              disabled={submitting || isExtracting}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void submitUpload(f);
              }}
              className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-surface-200 file:cursor-pointer"
            />
          </label>
        </section>

        {/* Extraction state */}
        {isExtracting && (
          <section className="mb-4 p-3 rounded-lg border border-surface-200 bg-surface-50 text-xs">
            <div className="flex items-center gap-2 text-surface-700">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-surface-400 border-t-surface-700" />
              Reading the receipt…
            </div>
          </section>
        )}

        {extractError && (
          <section className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
            Couldn&apos;t read the file ({extractError}). You can still attach it — your reviewer
            will fill in the details.
          </section>
        )}

        {/* Extracted summary + variance */}
        {extractedFields && (
          <section className="mb-4 p-3 rounded-lg border border-surface-200 bg-surface-50 text-xs">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="text-[11px] text-surface-500 uppercase tracking-wide">Receipt</div>
                <div className="text-sm font-medium text-surface-900 truncate">
                  {extractedFields.vendorName ?? 'Unknown vendor'}
                </div>
                <div className="text-surface-500 font-mono">
                  {receiptAmount != null
                    ? formatCurrency(receiptAmount, 2, extractedFields.currency ?? item.currency)
                    : '—'}
                  {extractedFields.documentDate
                    ? ` · ${formatDate(extractedFields.documentDate)}`
                    : ''}
                </div>
              </div>
              {variance != null && (
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {receiptMatchPercent != null && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] font-medium tabular-nums ${
                        receiptMatchPercent === 100
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                          : receiptMatchPercent >= 90
                            ? 'border-amber-300 bg-amber-50 text-amber-700'
                            : 'border-surface-200 text-surface-500'
                      }`}
                    >
                      {receiptMatchPercent}% match
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      varianceAbs! < 0.01
                        ? 'border-emerald-300 text-emerald-700'
                        : 'border-amber-300 text-amber-700'
                    }`}
                  >
                    {varianceAbs! < 0.01
                      ? 'Matches bank line'
                      : `Variance: ${variance > 0 ? '+' : ''}${formatCurrency(variance, 2, item.currency)}`}
                  </Badge>
                </div>
              )}
            </div>
            {needsAdjustment && !adjustmentOpen && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs mt-2"
                onClick={() => {
                  setAdjustmentOpen(true);
                  setAdjustmentAmount(String(varianceAbs!.toFixed(2)));
                }}
              >
                Explain the {formatCurrency(varianceAbs!, 2, item.currency)} difference
              </Button>
            )}
          </section>
        )}

        {/* Adjustment section */}
        {adjustmentOpen && (
          <section className="mb-4 p-3 rounded-lg border border-surface-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-surface-700 uppercase tracking-wide">
                Variance adjustment
              </span>
              <button
                type="button"
                onClick={() => setAdjustmentOpen(false)}
                className="text-[11px] text-surface-500 hover:text-surface-700"
              >
                Remove
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Select
                value={adjustmentType}
                onValueChange={(v) => setAdjustmentType(v as AdjustmentType)}
              >
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Adjustment type" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ADJUSTMENT_TYPE_LABELS) as AdjustmentType[]).map((t) => (
                    <SelectItem key={t} value={t} className="text-xs">
                      {ADJUSTMENT_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="text-xs"
              />
            </div>
            <Input
              value={adjustmentDescription}
              onChange={(e) => setAdjustmentDescription(e.target.value)}
              placeholder="Optional note (e.g. Stripe fee deducted before deposit)"
              className="text-xs"
            />
            <p className="text-[10px] text-surface-500 mt-2 italic">
              Recorded with the match for the reviewer. (QBO journal-entry sync is a pending follow-up.)
            </p>
          </section>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!selectedDocId || submitting || isExtracting}
            onClick={submit}
          >
            {submitting ? 'Attaching…' : 'Attach receipt'}
          </Button>
        </div>
      </div>
    </div>
  );
}
